import {
    TriageSummary,
    PrecisionOverview,
    DQSummary,
    QASummary,
    ConversionRunSummary,
    Dora,
    PRSummary,
    Runbook,
    FeatureFlag,
    IncidentTemplate,
    AppDockEntry,
} from '../types';

// Build-time default, can be overridden at runtime via function parameters
const DEMO_DEFAULT = import.meta.env.VITE_ENGINEERING_BRIDGE_DEMO === 'true';

// Helper to determine if demo mode is active
const isDemoMode = (isDemo?: boolean): boolean => {
    return isDemo !== undefined ? isDemo : DEMO_DEFAULT;
};

// Mock data for demo mode
const mockTriageSummary: TriageSummary = {
    asOf: new Date().toISOString(),
    services: [
        { name: 'conversion-engine', status: 'healthy', errorRate: 0.02, p95ms: 1200, p99ms: 2500 },
        { name: 'data-processor', status: 'degraded', errorRate: 0.15, p95ms: 800, p99ms: 1800 },
        { name: 'validation-service', status: 'healthy', errorRate: 0.01, p95ms: 600, p99ms: 1200 },
    ],
    queues: [
        { name: 'conversion-queue', depth: 45, p95WaitSec: 30 },
        { name: 'validation-queue', depth: 12, p95WaitSec: 15 },
    ],
    mttrHours7d: 2.5,
};

const mockPrecisionOverview: PrecisionOverview = {
    asOf: new Date().toISOString(),
    totalMappings: 15420,
    activeMappings: 12850,
    coveragePercent: 83.3,
    accuracyScore: 94.7,
    recentUpdates: 234,
    pendingReviews: 18,
};

const mockDQSummary: DQSummary = {
    asOf: new Date().toISOString(),
    totalRecords: 1250000,
    validRecords: 1187500,
    qualityScore: 95.0,
    criticalIssues: 3,
    warnings: 47,
    lastValidation: new Date().toISOString(),
};

const mockQASummary: QASummary = {
    asOf: new Date().toISOString(),
    totalTests: 2847,
    passingTests: 2745,
    failingTests: 102,
    coveragePercent: 87.3,
    lastRun: new Date().toISOString(),
    avgDuration: 45.2,
};

const mockConversionRunSummary: ConversionRunSummary = {
    asOf: new Date().toISOString(),
    totalRuns: 1250,
    successfulRuns: 1180,
    failedRuns: 70,
    avgDuration: 12.5,
    throughput: 45.2,
    errorRate: 5.6,
};

const mockDora: Dora = {
    deploymentFrequency: 'daily',
    leadTimeForChanges: 4.2,
    mttr: 2.1,
    changeFailureRate: 0.08,
};

const mockPRSummary: PRSummary = {
    totalPRs: 89,
    openPRs: 23,
    mergedPRs: 66,
    avgReviewTime: 8.5,
    approvalRate: 92.1,
};

const mockRunbooks: Runbook[] = [
    {
        id: 'rb-001',
        name: 'Database Recovery',
        description: 'Emergency database recovery procedures',
        lastUpdated: '2024-01-15',
    },
    {
        id: 'rb-002',
        name: 'Service Restart',
        description: 'Safe service restart protocols',
        lastUpdated: '2024-01-10',
    },
    {
        id: 'rb-003',
        name: 'Data Validation',
        description: 'Data quality validation steps',
        lastUpdated: '2024-01-08',
    },
];

const mockFeatureFlags: FeatureFlag[] = [
    {
        id: 'ff-001',
        name: 'New Validation Engine',
        description: 'Enable new data validation engine',
        enabled: true,
        lastModified: '2024-01-12',
    },
    {
        id: 'ff-002',
        name: 'Enhanced Logging',
        description: 'Enable enhanced logging for debugging',
        enabled: false,
        lastModified: '2024-01-10',
    },
    {
        id: 'ff-003',
        name: 'Performance Mode',
        description: 'Enable performance optimization mode',
        enabled: true,
        lastModified: '2024-01-08',
    },
];

const mockIncidentTemplates: IncidentTemplate[] = [
    {
        id: 'it-001',
        name: 'Data Quality Issue',
        description: 'Template for data quality incidents',
        severity: 'medium',
        lastUsed: '2024-01-14',
    },
    {
        id: 'it-002',
        name: 'Service Outage',
        description: 'Template for service outage incidents',
        severity: 'high',
        lastUsed: '2024-01-12',
    },
    {
        id: 'it-003',
        name: 'Performance Degradation',
        description: 'Template for performance issues',
        severity: 'low',
        lastUsed: '2024-01-10',
    },
];

const mockAppDockEntries: AppDockEntry[] = [
    {
        id: 'app-001',
        name: 'Data Explorer',
        description: 'Interactive data exploration tool',
        url: '/data-explorer',
        icon: 'Database',
    },
    {
        id: 'app-002',
        name: 'Metrics Dashboard',
        description: 'Real-time metrics visualization',
        url: '/metrics',
        icon: 'Gauge',
    },
    {
        id: 'app-003',
        name: 'Log Analyzer',
        description: 'Advanced log analysis tool',
        url: '/logs',
        icon: 'Search',
    },
];

// System Triage & Health Monitoring
export async function fetchTriageSummary(isDemo?: boolean): Promise<TriageSummary> {
    if (isDemoMode(isDemo)) {
        return mockTriageSummary;
    }

    try {
        const response = await fetch('/api/engineering/triage/summary');
        if (!response.ok) throw new Error('Failed to fetch triage summary');
        return response.json();
    } catch (error) {
        console.error('Error fetching triage summary:', error);
        throw error;
    }
}

// Precision Data
export async function fetchPrecisionOverview(isDemo?: boolean): Promise<PrecisionOverview> {
    if (isDemoMode(isDemo)) {
        return mockPrecisionOverview;
    }

    try {
        const response = await fetch('/api/engineering/precision/overview');
        if (!response.ok) throw new Error('Failed to fetch precision overview');
        return response.json();
    } catch (error) {
        console.error('Error fetching precision overview:', error);
        throw error;
    }
}

// Health Analysis
export async function fetchDQSummary(isDemo?: boolean): Promise<DQSummary> {
    if (isDemoMode(isDemo)) {
        return mockDQSummary;
    }

    try {
        const response = await fetch('/api/engineering/dq/summary');
        if (!response.ok) throw new Error('Failed to fetch DQ summary');
        return response.json();
    } catch (error) {
        console.error('Error fetching DQ summary:', error);
        throw error;
    }
}

// QA Dashboard
export async function fetchQASummary(isDemo?: boolean): Promise<QASummary> {
    if (isDemoMode(isDemo)) {
        return mockQASummary;
    }

    try {
        const response = await fetch('/api/engineering/qa/overview');
        if (!response.ok) throw new Error('Failed to fetch QA summary');
        return response.json();
    } catch (error) {
        console.error('Error fetching QA summary:', error);
        throw error;
    }
}

// ConversionOps
export async function fetchConversionRunSummary(isDemo?: boolean): Promise<ConversionRunSummary> {
    if (isDemoMode(isDemo)) {
        return mockConversionRunSummary;
    }

    try {
        const response = await fetch('/api/conversionops/runs/summary');
        if (!response.ok) throw new Error('Failed to fetch conversion run summary');
        return response.json();
    } catch (error) {
        console.error('Error fetching conversion run summary:', error);
        throw error;
    }
}

// CodeOps
export async function fetchDora(isDemo?: boolean): Promise<Dora> {
    if (isDemoMode(isDemo)) {
        return mockDora;
    }

    try {
        const response = await fetch('/api/codeops/dora');
        if (!response.ok) throw new Error('Failed to fetch DORA metrics');
        return response.json();
    } catch (error) {
        console.error('Error fetching DORA metrics:', error);
        throw error;
    }
}

export async function fetchPRSummary(isDemo?: boolean): Promise<PRSummary> {
    if (isDemoMode(isDemo)) {
        return mockPRSummary;
    }

    try {
        const response = await fetch('/api/codeops/prs/summary');
        if (!response.ok) throw new Error('Failed to fetch PR summary');
        return response.json();
    } catch (error) {
        console.error('Error fetching PR summary:', error);
        throw error;
    }
}

// Operations Control
export async function fetchRunbooks(isDemo?: boolean): Promise<Runbook[]> {
    if (isDemoMode(isDemo)) {
        return mockRunbooks;
    }

    try {
        const response = await fetch('/api/ops/runbooks');
        if (!response.ok) throw new Error('Failed to fetch runbooks');
        return response.json();
    } catch (error) {
        console.error('Error fetching runbooks:', error);
        throw error;
    }
}

export async function fetchFeatureFlags(isDemo?: boolean): Promise<FeatureFlag[]> {
    if (isDemoMode(isDemo)) {
        return mockFeatureFlags;
    }

    try {
        const response = await fetch('/api/ops/feature-flags');
        if (!response.ok) throw new Error('Failed to fetch feature flags');
        return response.json();
    } catch (error) {
        console.error('Error fetching feature flags:', error);
        throw error;
    }
}

export async function fetchIncidentTemplates(isDemo?: boolean): Promise<IncidentTemplate[]> {
    if (isDemoMode(isDemo)) {
        return mockIncidentTemplates;
    }

    try {
        const response = await fetch('/api/ops/incident-templates');
        if (!response.ok) throw new Error('Failed to fetch incident templates');
        return response.json();
    } catch (error) {
        console.error('Error fetching incident templates:', error);
        throw error;
    }
}

// App Dock
export async function fetchAppDockEntries(isDemo?: boolean): Promise<AppDockEntry[]> {
    if (isDemoMode(isDemo)) {
        return mockAppDockEntries;
    }

    try {
        const response = await fetch('/api/app-dock/entries');
        if (!response.ok) throw new Error('Failed to fetch app dock entries');
        return response.json();
    } catch (error) {
        console.error('Error fetching app dock entries:', error);
        throw error;
    }
}
